import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  RealtimeEventTypes,
  RealtimeTopics,
} from '@interfaces/realtime/realtime-envelope.interface';
import {
  PartnerBankJourneyCardResponse,
  PartnerBankJourneyResponse,
} from '@interfaces/partners/responses/partner-bank-journey.response';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { RealtimeService } from '@services/realtime/realtime.service';
import { BriefingOnlyPartnerDealChanges } from '@interfaces/realtime/events/partner-deal.events';
import { Observable, Subscription, filter } from 'rxjs';

const EMPTY_JOURNEY: PartnerBankJourneyResponse = { unlocked: false, deals: [], cards: [] };

/** O que o store está acompanhando: um deal (tela do deal) ou todos os deals (tela Meus deals, card 3765). */
type JourneyScope = { kind: 'deal'; dealId: string } | { kind: 'all' };

/**
 * **Jornada nos bancos**, lida uma vez e compartilhada pelos componentes que a mostram. Provida pela tela que a usa
 * (`providers`), então vive e morre com ela.
 *
 * - **Um deal** (`setDeal`, tela do deal, card 3707): aba/visão Bancos, cartão da Visão geral e painel de
 *   comentários. Tempo real: relê em silêncio a cada `partner-deal.changed` do tópico `partner-deal:{id}` (o backend
 *   publica `bank-journey-changed` ao ligar uma negociação, mover o banco de etapa e comentar).
 * - **Todos os deals** (`setAllDeals`, tela Meus deals, card 3765): os bancos de todos os deals que quem pede vê,
 *   pela mesma régua do quadro de deals. Sem tópico próprio: quem relê é a tela (`reload`), quando o tempo real do
 *   quadro de deals avisa que algo mudou.
 * - `cards` são os bancos **depois dos filtros** (deal, banco e texto, só usados no modo todos); `allCards`, todos.
 *   `unlocked` olha `allCards`: filtro que não casa com nada não trava o seletor.
 * - O banco aberto no painel é guardado pelo id, para sobreviver à releitura e ao filtro.
 */
@Injectable()
export class DealBankJourneyStore {
  private readonly partnersService = inject(PartnersHttpService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly realtime = inject(RealtimeService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly journeySignal = signal<PartnerBankJourneyResponse>(EMPTY_JOURNEY);
  readonly journey = this.journeySignal.asReadonly();
  /** Deals com pelo menos um banco (para o filtro do modo todos). */
  readonly deals = computed(() => this.journeySignal().deals);
  readonly allCards = computed(() => this.journeySignal().cards);
  readonly unlocked = computed(() => this.journeySignal().unlocked && this.allCards().length > 0);

  /** Filtro por deal (`partnerDealId`); vazio = todos. */
  readonly dealFilter = signal<string[]>([]);
  /** Filtro por banco (nome); vazio = todos. */
  readonly bankFilter = signal<string[]>([]);
  /** Texto da busca: casa com o banco, a empresa ou o número do deal. */
  readonly textFilter = signal('');

  readonly filtering = computed(
    () =>
      this.dealFilter().length > 0 ||
      this.bankFilter().length > 0 ||
      this.textFilter().trim().length > 0
  );

  readonly cards = computed(() => {
    const deals = new Set(this.dealFilter());
    const banks = new Set(this.bankFilter());
    const text = normalize(this.textFilter());
    return this.allCards().filter(
      card =>
        (deals.size === 0 || deals.has(card.partnerDealId)) &&
        (banks.size === 0 || banks.has(card.bankName ?? '')) &&
        (!text ||
          normalize(
            `${card.bankName ?? ''} ${card.companyName} #${card.partnerDealSequentialId}`
          ).includes(text))
    );
  });

  /**
   * A primeira leitura terminou (com sucesso ou não). Até lá o seletor (card 3752) não mostra o cadeado: sem isto,
   * a tela piscaria "travado" enquanto a jornada carrega.
   */
  private readonly loadedSignal = signal(false);
  readonly loaded = this.loadedSignal.asReadonly();

  private readonly selectedCardId = signal<string | null>(null);
  /** Banco com os comentários abertos, ou `null`. */
  readonly selectedCard = computed<PartnerBankJourneyCardResponse | null>(
    () => this.allCards().find(card => card.pipelineDealId === this.selectedCardId()) ?? null
  );

  private scope: JourneyScope | null = null;
  private request: Subscription | null = null;
  private realtimeSubscription: Subscription | null = null;

  /** Passa a acompanhar o deal: zera o que era do anterior, lê a jornada e escuta o tempo real dele. */
  setDeal(dealId: string | null): void {
    if (this.scope?.kind === 'deal' && this.scope.dealId === dealId) return;

    this.reset(dealId ? { kind: 'deal', dealId } : null);
    if (!dealId) return;

    this.load(false);
    this.realtimeSubscription = this.realtime
      .on(RealtimeEventTypes.PartnerDealChanged, RealtimeTopics.partnerDeal(dealId))
      .pipe(
        // O Briefing (card 3878) e a investigação (card 3918) avisam a cada fase ou progresso e não mexem na jornada.
        filter(
          envelope =>
            !BriefingOnlyPartnerDealChanges.includes(String(envelope.payload?.change ?? ''))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.reload());
  }

  /** Passa a acompanhar todos os deals de quem pede (tela Meus deals, card 3765). */
  setAllDeals(): void {
    if (this.scope?.kind === 'all') return;
    this.reset({ kind: 'all' });
    this.load(false);
  }

  /** Releitura em silêncio (tempo real, depois de comentar): falha não mostra toast, a jornada fica como estava. */
  reload(): void {
    this.load(true);
  }

  clearFilters(): void {
    this.dealFilter.set([]);
    this.bankFilter.set([]);
    this.textFilter.set('');
  }

  openComments(card: PartnerBankJourneyCardResponse): void {
    this.selectedCardId.set(card.pipelineDealId);
  }

  closeComments(): void {
    this.selectedCardId.set(null);
  }

  private reset(scope: JourneyScope | null): void {
    this.scope = scope;
    this.journeySignal.set(EMPTY_JOURNEY);
    this.loadedSignal.set(false);
    this.selectedCardId.set(null);
    this.clearFilters();
    this.request?.unsubscribe();
    this.realtimeSubscription?.unsubscribe();
    this.realtimeSubscription = null;
  }

  /** Lê a jornada do escopo atual. Uma leitura nova cancela a anterior. */
  private load(silent: boolean): void {
    const scope = this.scope;
    if (!scope) return;

    const source$: Observable<PartnerBankJourneyResponse> =
      scope.kind === 'deal'
        ? this.partnersService.getDealBankJourney(scope.dealId)
        : this.partnersService.getBankJourney();

    this.request?.unsubscribe();
    this.request = source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: journey => {
        if (scope !== this.scope) return;
        this.journeySignal.set(journey);
        this.loadedSignal.set(true);
      },
      error: error => {
        if (scope === this.scope) this.loadedSignal.set(true);
        if (!silent) {
          this.formErrorHandler.showToast(
            error,
            scope.kind === 'deal'
              ? 'Não foi possível carregar os bancos deste deal. Recarregue a página para tentar de novo.'
              : 'Não foi possível carregar os bancos dos seus deals. Recarregue a página para tentar de novo.'
          );
        }
      },
    });
  }
}

/** Minúsculas e sem acento, para a busca casar "itau" com "Itaú". */
function normalize(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}
